import React, { useEffect, useId, useState } from "react";
import "./InputField.css";
import EyeOff from "../../icons/EyeOff.svg";
import EyeOn from "../../icons/EyeOn.svg";
import Lock from "../../icons/Lock.svg";

export default function InputField({
  type = "text",
  placeholder,
  label = "",
  disabled = false,
  id = "",
  mandatory,
  value,
  language,
  onChange,
  onBlur = () => { },
  touched = false,
  errorMsg = "",
  ignoreTouched = false,
  childrenIcon = <></>,
  inputClassName = "",
  labelClassName = "",
  placeholderClassName = "",
  placeholderFocusedClassName = "",
  nativePlaceholder = false,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const generatedId = useId().replace(/:/g, "");
  const inputId = id || `inputfield-${generatedId}`;
  const containerId = id ? `${id}-container` : null;
  const visibilityToggleId = id ? `${id}-visibility-toggle` : undefined;
  const errorLabelId = `${inputId}-error-label`;
  const errorMessageId = `${inputId}-error-message`;

  const handleFocus = () => {
    setIsInputFocused(true);
  };

  const handleBlur = () => {
    setIsInputFocused(value || false);
    onBlur();
  };

  const handleOnChange = (event) => {
    onChange(event.target.value);
  };

  useEffect(() => {
    setIsInputFocused(value || isInputFocused);
  }, [value]);

  return (
    <>
      {label ? (
        <div id={containerId} className="flex flex-col gap-[8px] w-full">
          <label htmlFor={inputId} className="flex items-center gap-[4px] leading-none">
            <span className="font-poppins text-[14px] font-normal text-[#616975]">{label}</span>
            {mandatory && <span className="font-poppins text-[16px] font-normal text-[#CC0000]">*</span>}
          </label>
          <div className="relative w-full">
            <input
              id={inputId}
              onBlur={handleBlur}
              value={value}
              className={`w-full h-[44px] px-[16px] bg-white border border-solid rounded-[12px] font-poppins text-[16px] font-normal text-[#1a1a1a] transition-colors duration-200 box-border outline-none placeholder:text-[#99A3B2] placeholder:font-normal ${
                errorMsg.length > 0 && (touched || ignoreTouched)
                  ? "border-[var(--primary-danger)] focus:border-[var(--primary-danger)]"
                  : "border-[#DEE3ED] focus:border-[#99A3B2]"
              } ${inputClassName}`}
              onChange={handleOnChange}
              type={isVisible ? "text" : type}
              onFocus={handleFocus}
              placeholder={placeholder}
              disabled={disabled}
            />
            {type === "password" && (
              <img
                src={isVisible ? EyeOn : EyeOff}
                alt="show-hide-password"
                title={isVisible ? "Click to hide password" : "Click to show password"}
                className="absolute w-[20px] h-[20px] right-[16px] top-1/2 [transform:translateY(-50%)] cursor-pointer opacity-40"
                onClick={() => setIsVisible(!isVisible)}
              />
            )}
            {childrenIcon ? childrenIcon : null}
          </div>
          {errorMsg.length > 0 && (touched || ignoreTouched) && (
            <p id={errorMessageId} className="inputfield-error-message">{errorMsg}</p>
          )}
        </div>
      ) : disabled ? (
        <div id={containerId} className={"inputfield-container-disabled"}>
          <label id={id ? `${id}-label` : null} htmlFor={inputId} className="inputfield-label-placeholder">
            <span className={isInputFocused ? "inputfield-placeholder-focused" : "inputfield-placeholder"}>
              {placeholder}
              <span className="credential-input-mandatory-astrix">
                {mandatory ? "*" : null}
              </span>
            </span>
          </label>

          <input
            id={inputId}
            onBlur={handleBlur}
            value={value}
            disabled={true}
            className="inputfield-input-disabled"
            type={type}
          />
          
          {childrenIcon ? childrenIcon : <img src={Lock} alt="lock" />}
        </div>
      ) : (
        <div id={containerId} className={"inputfield-container"}>
          {!nativePlaceholder && (
            <label id={id ? `${id}-label` : null} htmlFor={inputId} className={`inputfield-label-placeholder ${labelClassName}`}>
              <span className={`${isInputFocused ? `inputfield-placeholder-focused ${placeholderFocusedClassName}` : `inputfield-placeholder ${placeholderClassName}`}`}>
                {placeholder}
                <span className="credential-input-mandatory-astrix">
                  {mandatory ? "*" : null}
                </span>
              </span>
            </label>
          )}
          <input
            id={inputId}
            onBlur={handleBlur}
            value={value}
            className={`${errorMsg.length === 0 || (!touched && !ignoreTouched) ? "inputfield-input" : "inputfield-input-error"} ${inputClassName}`}
            onChange={handleOnChange}
            type={isVisible ? "text" : type}
            onFocus={handleFocus}
            placeholder={nativePlaceholder ? placeholder : undefined}
          />

          {type === "password" && (
            <img
              id={visibilityToggleId}
              src={isVisible ? EyeOn : EyeOff}
              alt="show-hide-password"
              title={isVisible ? "Click to hide password" : "Click to show password"}
              className="inputfield-visibility-icon"
              onClick={() => setIsVisible(!isVisible)}
            />
          )}


          {childrenIcon ? childrenIcon : null}


          <div id={errorLabelId} className="inputfield-error-label">
            {errorMsg.length === 0 || (!touched && !ignoreTouched ) ? null : (
              <p id={errorMessageId} className="inputfield-error-message"> {errorMsg} </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
